// Chakra imports
import { Portal, Box, useDisclosure } from '@chakra-ui/react';
import Footer from 'components/footer/FooterAdmin.js';
// Layout components
import Navbar from 'components/navbar/NavbarAdmin.js';
import Sidebar from 'components/sidebar/Sidebar.js';
import { SidebarContext } from 'contexts/SidebarContext';
import React, { useState, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import routes from 'routes.js';
import { fetchAdminProfile } from '../../features/admin/rbacSlice';
import { getPendingViewCounts } from '../../features/admin/pendingViewCountsSlice';
import WebsiteDataDetails from 'views/admin/websiteData/WebsiteDataDetails';
import ProjectBidDetail from 'views/admin/projectBids/ProjectBidDetail';
import ProjectBidDetailWrapper from 'views/admin/projectBids/ProjectBidDetailWrapper';
import NewOrderDetail from 'views/admin/projectBids/NewOrderDetail';
import ProjectDeliveryDetail from 'views/admin/projectBids/ProjectDeliveryDetail';
import SalesOrder from 'views/admin/salesOrder';
import Invoice from 'views/admin/invoice';
import CreateClientProject from 'views/admin/projects/CreateClientProject';
import UpdateClientProject from 'views/admin/projects/UpdateClientProject';
import ViewClientProject from 'views/admin/projects/ViewClientProject';
import FreelancerDetailPage from 'views/admin/freelancers/FreelancerDetailPage';

// Custom Chakra theme
export default function Dashboard(props) {
  const { ...rest } = props;
  const location = useLocation();
  const dispatch = useDispatch();
  const { permissions, admin } = useSelector((state) => state.rbac || { permissions: {}, admin: null });
  // states and functions
  const [fixed] = useState(false);
  const sidebarWidth = '260px';
  const [areModulesExpanded, setAreModulesExpanded] = useState(true);

  // Fetch pending view counts + admin profile (permissions) when admin layout loads
  useEffect(() => {
    dispatch(getPendingViewCounts());
    dispatch(fetchAdminProfile());
  }, [dispatch]);
  // functions for changing the states from components
  const getRoute = () => {
    return window.location.pathname !== '/admin/full-screen-maps';
  };
  const getActiveRoute = (routes, currentPath) => {
    let activeRoute = 'Default Brand Text';

    if (/^\/admin\/freelancer\/[^/]+$/.test(currentPath)) {
      return 'Freelancer Details';
    }

    if (/^\/admin\/update-client-project\/[^/]+$/.test(currentPath)) {
      return 'Update Project';
    }

    if (/^\/admin\/view-client-project\/[^/]+$/.test(currentPath)) {
      return 'View Project';
    }

    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = getActiveRoute(routes[i].items, currentPath);
        if (collapseActiveRoute !== 'Default Brand Text') {
          return collapseActiveRoute;
        }
      } else if (routes[i].category) {
        let categoryActiveRoute = getActiveRoute(routes[i].items, currentPath);
        if (categoryActiveRoute !== 'Default Brand Text') {
          return categoryActiveRoute;
        }
      } else {
        if (routes[i].layout && routes[i].path && routes[i].name) {
          const fullPath = routes[i].layout + routes[i].path;
          // Check if current path matches the route path
          if (currentPath === fullPath || currentPath.startsWith(fullPath + '/')) {
          return routes[i].name;
          }
        }
      }
    }
    return activeRoute;
  };
  const getActiveNavbar = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbar(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else if (routes[i].category) {
        let categoryActiveNavbar = getActiveNavbar(routes[i].items);
        if (categoryActiveNavbar !== activeNavbar) {
          return categoryActiveNavbar;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].secondary;
        }
      }
    }
    return activeNavbar;
  };
  const getActiveNavbarText = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbarText(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else if (routes[i].category) {
        let categoryActiveNavbar = getActiveNavbarText(routes[i].items);
        if (categoryActiveNavbar !== activeNavbar) {
          return categoryActiveNavbar;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].messageNavbar;
        }
      }
    }
    return activeNavbar;
  };
  const hasPermission = (moduleKey, action = 'view') => {
    if (!admin) return false;
    if (admin.role === 'super_admin') return true;
    const modulePerms = permissions && permissions[moduleKey];
    if (modulePerms === true) return true;
    if (!modulePerms || typeof modulePerms !== 'object') return false;
    return !!(modulePerms[action] || modulePerms.view || modulePerms.create || modulePerms.edit || modulePerms.delete);
  };

  const getRoutes = (routesConfig) => {
    return routesConfig.map((route, key) => {
      if (route.layout === '/admin' && route.path && !route.hidden) {
        const moduleKey = `${route.layout}${route.path}`;
        if (!hasPermission(moduleKey, 'view')) {
          return null;
        }
        return (
          <Route path={`${route.path}`} element={route.component} key={key} />
        );
      }
      if (route.collapse || route.category) {
        return getRoutes(route.items);
      }
      return null;
    });
  };
  document.documentElement.dir = 'ltr';
  const { onOpen } = useDisclosure();
  document.documentElement.dir = 'ltr';
  return (
    <Box>
      <Box>
        <SidebarContext.Provider
          value={{
            sidebarWidth,
            areModulesExpanded,
            setAreModulesExpanded,
          }}
        >
          <Sidebar routes={routes} display="none" {...rest} />
          <Box
            float="right"
            minHeight="100vh"
            height="100%"
            overflow="auto"
            position="relative"
            maxHeight="100%"
            w={{ base: '100%', xl: `calc(100% - ${sidebarWidth})` }}
            maxWidth={{ base: '100%', xl: `calc(100% - ${sidebarWidth})` }}
            ml={{ base: '0', xl: sidebarWidth }}
            transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
            transitionDuration=".2s, .2s, .35s"
            transitionProperty="top, bottom, width, margin-left"
            transitionTimingFunction="linear, linear, ease"
          >
            <Portal>
              <Box>
                <Navbar
                  onOpen={onOpen}
                  logoText={''}
                  brandText={getActiveRoute(routes, location.pathname)}
                  secondary={getActiveNavbar(routes)}
                  message={getActiveNavbarText(routes)}
                  fixed={fixed}
                  {...rest}
                />
              </Box>
            </Portal>

            {getRoute() ? (
              <Box
                mx="auto"
                p={{ base: '20px', md: '10px' }}
                // pe="20px"
                minH="100vh"
                // pt="50px"
              >
                <Routes>
                  {getRoutes(routes)}
                  <Route
                    path="/website-data/:id/details"
                    element={<WebsiteDataDetails />}
                  />
                  <Route
                    path="/project-bids/:bidId/details"
                    element={<ProjectBidDetailWrapper />}
                  />
                  <Route
                    path="/project-finance/:bidId/details"
                    element={<NewOrderDetail />}
                  />
                  <Route
                    path="/project-delivery/:bidId/details"
                    element={<ProjectDeliveryDetail />}
                  />
                  <Route
                    path="/salesorder/:milestoneId/:projectbidId"
                    element={<SalesOrder />}
                  />
                  <Route
                    path="/invoice/:milestoneId/:projectbidId"
                    element={<Invoice />}
                  />
                  <Route
                    path="/create-client-project"
                    element={<CreateClientProject />}
                  />
                  <Route
                    path="/update-client-project/:projectId"
                    element={<UpdateClientProject />}
                  />
                  <Route
                    path="/view-client-project/:projectId"
                    element={<ViewClientProject />}
                  />
                  <Route
                    path="/freelancer/:userId"
                    element={<FreelancerDetailPage />}
                  />
                  <Route
                    path="/"
                    element={<Navigate to="/admin/default" replace />}
                  />
                </Routes>
              </Box>
            ) : null}
            {/* <Box>
              <Footer />
            </Box> */}
          </Box>
        </SidebarContext.Provider>
      </Box>
    </Box>
  );
}
